import React, { useState } from "react";

import { generateFunctionalUnitId } from './utils';
import FunctionalUnitCascadeModal from '../../shared/FunctionalUnitCascadeModal';

import Swal from "sweetalert2";
import { toast } from "react-toastify";

const confirmSimpleDelete = (unit) => Swal.fire({
  title: "Supprimer cette unité fonctionnelle ?",
  text: `"${unit.name || "Sans nom"}" sera définitivement supprimée.`,
  icon: "warning",
  showCancelButton: true,
  confirmButtonText: "Supprimer",
  cancelButtonText: "Annuler",
  confirmButtonColor: "#d33",
});

export default function FunctionalUnitsForm({ formData, setFormData, setBaselineFormData, buildingId }) {
  const [editingIndex, setEditingIndex] = useState(null);
  const [newName, setNewName] = useState("");
  // { unit, index, impact } le temps que l'utilisateur choisisse quoi faire des
  // sections/pièces/usages qui référencent encore l'unité qu'on supprime.
  const [cascadePrompt, setCascadePrompt] = useState(null);

  const functionalUnits = formData.functionalUnits || [];

  const updateName = (index, value) => {
    const updated = [...functionalUnits];
    updated[index].name = value;
    setFormData({ ...formData, functionalUnits: updated });
  };

  const removeUnitLocally = (index) => {
    const updatedUnits = functionalUnits.filter((_, i) => i !== index);
    setFormData({ ...formData, functionalUnits: updatedUnits });
    setEditingIndex(null);
  };

  const handleDelete = async (index) => {
    const unit = functionalUnits[index];

    // Bâtiment pas encore créé : aucune section/pièce/usage ne peut encore référencer
    // cette unité, la suppression reste purement locale (comportement inchangé).
    if (!buildingId) {
      const result = await confirmSimpleDelete(unit);
      if (!result.isConfirmed) return;
      removeUnitLocally(index);
      return;
    }

    let impact;
    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL}/get_functional_unit_impact/${buildingId}/${unit.id}`);
      if (!response.ok) throw new Error('Erreur API');
      impact = await response.json();
    } catch (error) {
      Swal.fire({
        title: "Erreur",
        text: "Impossible de vérifier l'utilisation de cette unité fonctionnelle. Réessayez.",
        icon: "error", confirmButtonText: "OK", customClass: "swal-custom",
      });
      return;
    }

    const impactCount = (impact.sections?.length || 0) + (impact.rooms?.length || 0) + (impact.usages?.length || 0);

    if (impactCount === 0) {
      const result = await confirmSimpleDelete(unit);
      if (!result.isConfirmed) return;
      removeUnitLocally(index);
      return;
    }

    setCascadePrompt({ unit, index, impact });
  };

  // Réaffecte (ou vide) les éléments sélectionnés, puis retire l'unité de la liste du
  // bâtiment. Écrit immédiatement en base (pas différé à l'enregistrement du formulaire
  // bâtiment) car la réaffectation touche d'autres collections que "buildings".
  const applyDeletion = async (reassignIds, newUnitId) => {
    const { index } = cascadePrompt;

    if (reassignIds) {
      try {
        const response = await fetch(`${process.env.REACT_APP_API_URL}/reassign_functional_unit`, {
          method: "PUT",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ ...reassignIds, new_unit_id: newUnitId }),
        });
        if (!response.ok) throw new Error('Erreur API');
      } catch (error) {
        toast.error("Erreur lors de la réaffectation des éléments concernés.");
        return;
      }
    }

    const updatedUnits = functionalUnits.filter((_, i) => i !== index);
    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL}/update_building`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ _id: buildingId, functional_units: updatedUnits }),
      });
      if (!response.ok) throw new Error('Erreur API');
    } catch (error) {
      toast.error("La suppression de l'unité fonctionnelle n'a pas pu être enregistrée.");
      return;
    }

    setFormData((prev) => ({ ...prev, functionalUnits: updatedUnits }));
    // Cette écriture étant déjà persistée, on aligne la baseline pour ne pas déclencher
    // à tort l'avertissement "modifications non enregistrées" sur le reste du formulaire.
    setBaselineFormData?.((prev) => ({ ...prev, functionalUnits: updatedUnits }));
    setCascadePrompt(null);
    setEditingIndex(null);
    toast.success("Unité fonctionnelle supprimée.");
  };

  const handleAdd = () => {
    if (!newName.trim()) return;

    const trimmedName = newName.trim();

    setFormData({
      ...formData,
      functionalUnits: [
        ...functionalUnits,
        {
          name: trimmedName,
          id: generateFunctionalUnitId(trimmedName),
        },
      ],
    });

    setNewName("");
  };

  return (
    <div className="functional-units-container">
      <h2>Unités fonctionnelles</h2>

      {/* LISTE */}
      {functionalUnits.map((unit, index) => (
        <div key={unit.id} className="functional-unit-card">
          {editingIndex === index ? (
            <input
              type="text"
              value={unit.name}
              onChange={(e) => updateName(index, e.target.value)}
              onBlur={() => setEditingIndex(null)}
              autoFocus
            />
          ) : (
            <strong>{unit.name || "Sans nom"}</strong>
          )}

          <div className="functional-unit-actions">
            <button onClick={() => setEditingIndex(index)}>✏️</button>
            <button onClick={() => handleDelete(index)}>🗑</button>
          </div>
        </div>
      ))}

      {/* AJOUT */}
      <div className="functional-unit-add">
        <input
          type="text"
          placeholder="Nom de l’unité fonctionnelle"
          value={newName}
          onChange={(e) => setNewName(e.target.value)}
        />
        <button
          className="small-black-button"
          onClick={handleAdd}
        >
          ➕ Ajouter une unité fonctionnelle
        </button>
      </div>

      {cascadePrompt && (
        <FunctionalUnitCascadeModal
          title="Cette unité fonctionnelle est encore utilisée"
          description={`"${cascadePrompt.unit.name || "Sans nom"}" est référencée par des sections, pièces ou usages. Choisissez une unité de remplacement (ou videz), puis sélectionnez ce qui doit être réaffecté.`}
          groups={[
            { key: 'sections', label: 'Sections', items: cascadePrompt.impact.sections.map((s) => ({ _id: s._id, label: s.label, matchesOld: true })) },
            { key: 'rooms', label: 'Pièces', items: cascadePrompt.impact.rooms.map((r) => ({ _id: r._id, label: r.label, matchesOld: true })) },
            { key: 'usages', label: 'Usages', items: cascadePrompt.impact.usages.map((u) => ({ _id: u._id, label: u.label, matchesOld: true })) },
          ]}
          targetOptions={[
            ...functionalUnits.filter((u) => u.id !== cascadePrompt.unit.id).map((u) => ({ id: u.id, label: u.name || 'Sans nom' })),
            { id: '', label: '— Vider (aucune unité) —' },
          ]}
          defaultAllChecked
          onConfirm={(selection, targetUnitId) => applyDeletion(
            { section_ids: selection.sections, room_ids: selection.rooms, usage_ids: selection.usages },
            targetUnitId
          )}
          onSkip={() => applyDeletion(null, null)}
          onCancel={() => setCascadePrompt(null)}
        />
      )}

    </div>
  );
}
